/**
 * CrossDatasetRollupSection — the DC-1702 P2 rollup view (epic DC-1625).
 * Spec: docs/design/v3.0-maturity-matrix-ux-spec.md §8 (addendum).
 *
 * The Data Readiness lens's domain-zoom cross-dataset read: is this FAIR gap
 * SYSTEMIC across the estate, or specific to one source system? Renders
 * computeDomainRollup's verdicts with the numbers and the rationale
 * in-payload — the verdict WORD carries the finding (never color-only), and
 * the reader judges the call with convergence / source-alignment / n beside
 * it.
 *
 * Collapsed by default and lazy-fetched on first expand — no new fetch on
 * the demo path. Once loaded, the collapsed row keeps a one-line summary.
 * Thresholds render from the payload (calibration candidates, never
 * re-hardcoded client-side). Tokens only (triangulation.css family,
 * lint:cream MIGRATED).
 */

import React, { useCallback, useRef, useState } from 'react';

import { useApiClient } from '../../hooks/useApiClient';

// ---------------------------------------------------------------- contract
// Mirrors server/triangulation/crossDatasetRollup.ts (DomainRollup).

type PillarVerdict =
  | 'systemic'
  | 'source-specific'
  | 'mixed'
  | 'indeterminate-single-source'
  | 'insufficient';

interface RollupSourceGroupStat {
  source_system_id: string | null;
  label: string;
  vendor: string | null;
  dataset_count: number;
  evaluated_count: number;
  mean: number | null;
}

interface PillarRollup {
  pillar: string;
  evaluated_count: number;
  mean: number | null;
  min: number | null;
  max: number | null;
  variance: number | null;
  convergence: number | null;
  source_alignment: number | null;
  verdict: PillarVerdict;
  rationale: string;
  by_source_system: RollupSourceGroupStat[];
}

interface DomainRollup {
  domain_id: string;
  coverage: {
    total: number;
    evaluated: number;
    attributed: number;
    unattributed: number;
  };
  thresholds: {
    convergence_systemic: number;
    source_alignment: number;
  };
  pillars: PillarRollup[];
  overall: {
    verdict: PillarVerdict;
    rationale: string;
  };
}

// ---------------------------------------------------------------- helpers

const VERDICT_WORDS: Record<PillarVerdict, string> = {
  systemic: 'systemic',
  'source-specific': 'source-specific',
  mixed: 'mixed',
  'indeterminate-single-source': 'indeterminate — single source',
  insufficient: 'insufficient',
};

function verdictClass(v: PillarVerdict): string {
  switch (v) {
    case 'systemic':
      return 'tri-xdr-verdict tri-xdr-verdict--systemic';
    case 'source-specific':
    case 'mixed':
      return 'tri-xdr-verdict tri-xdr-verdict--divergent';
    default:
      return 'tri-xdr-verdict tri-xdr-verdict--indeterminate';
  }
}

const fmt = (n: number | null, digits = 2): string => (n === null ? '—' : n.toFixed(digits));

// ---------------------------------------------------------------- pillar row

function PillarRow({ pillar }: { pillar: PillarRollup }): React.ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <div className="tri-xdr-pillar" data-testid={`xdr-pillar-${pillar.pillar}`}>
      <div className="tri-xdr-pillar-head">
        <span className="tri-xdr-pillar-name">{pillar.pillar}</span>
        <span className={verdictClass(pillar.verdict)}>{VERDICT_WORDS[pillar.verdict]}</span>
        <span className="tri-xdr-nums">
          mean {fmt(pillar.mean)} · convergence {fmt(pillar.convergence)} · source-alignment{' '}
          {fmt(pillar.source_alignment)} · n {pillar.evaluated_count}
        </span>
        {pillar.by_source_system.length > 0 && (
          <button
            type="button"
            className="tri-xdr-text-btn"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide sources' : 'By source system'}
          </button>
        )}
      </div>
      <p className="tri-xdr-rationale">{pillar.rationale}</p>
      {open && (
        <table className="tri-xdr-table">
          <caption className="tri-xdr-visually-hidden">
            Per-source-system dataset counts and mean scores for the {pillar.pillar} pillar
          </caption>
          <thead>
            <tr>
              <th scope="col">Source system</th>
              <th scope="col">Vendor</th>
              <th scope="col" className="tri-xdr-num">Datasets (evaluated/total)</th>
              <th scope="col" className="tri-xdr-num">Mean</th>
            </tr>
          </thead>
          <tbody>
            {pillar.by_source_system.map((g) => (
              <tr key={`${g.source_system_id ?? g.label}`}>
                <td>
                  {g.source_system_id === null ? (
                    <em className="tri-xdr-muted">{g.label}</em>
                  ) : (
                    g.label
                  )}
                </td>
                <td>{g.vendor ?? '—'}</td>
                <td className="tri-xdr-num">
                  {g.evaluated_count} / {g.dataset_count}
                </td>
                <td className="tri-xdr-num">{fmt(g.mean)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- section

export function CrossDatasetRollupSection({ domainId }: { domainId: string }): React.ReactElement {
  const { authFetch } = useApiClient();
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState<'idle' | 'loading' | 'error' | 'ready'>('idle');
  const [rollup, setRollup] = useState<DomainRollup | null>(null);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState('loading');
    try {
      const res = await authFetch(`/api/triangulation/domain/${domainId}/rollup`);
      if (requestSeq.current !== seq) return;
      if (!res.ok) throw new Error(`rollup request failed (${res.status})`);
      const body = (await res.json()) as DomainRollup;
      if (requestSeq.current !== seq) return;
      setRollup(body);
      setState('ready');
    } catch {
      if (requestSeq.current !== seq) return;
      setState('error');
    }
  }, [authFetch, domainId]);

  const toggle = (): void => {
    const next = !expanded;
    setExpanded(next);
    if (next && state === 'idle') void load();
  };

  const exportCsv = useCallback(async () => {
    try {
      const res = await authFetch(`/api/triangulation/domain/${domainId}/rollup/export`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fair-rollup_${domainId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Export is a convenience affordance; a failed download must not take
      // the section down. The rollup itself stays on screen.
    }
  }, [authFetch, domainId]);

  return (
    <section className="tri-xdr" data-testid="cross-dataset-rollup">
      <div className="tri-xdr-head">
        <div className="tri-section-label">Cross-dataset rollup</div>
        {!expanded && rollup && (
          <span className="tri-xdr-summary" data-testid="xdr-summary">
            {VERDICT_WORDS[rollup.overall.verdict]} · {rollup.coverage.evaluated} of{' '}
            {rollup.coverage.total} datasets evaluated, {rollup.coverage.attributed} attributed
          </span>
        )}
        <button
          type="button"
          className="tri-xdr-text-btn"
          aria-expanded={expanded}
          onClick={toggle}
        >
          {expanded ? 'Hide rollup' : 'Show rollup'}
        </button>
      </div>

      {expanded && state === 'loading' && (
        <div className="tri-xdr-skeleton" role="status" aria-label="Loading cross-dataset rollup" />
      )}

      {expanded && state === 'error' && (
        <div className="tri-xdr-error" role="alert">
          <span>Couldn’t load the cross-dataset rollup.</span>
          <button type="button" className="tri-xdr-text-btn" onClick={() => void load()}>
            Retry
          </button>
        </div>
      )}

      {expanded && state === 'ready' && rollup && (
        <div className="tri-xdr-body">
          <div className="tri-xdr-overall">
            <span className={verdictClass(rollup.overall.verdict)}>
              {VERDICT_WORDS[rollup.overall.verdict]}
            </span>
            <span className="tri-xdr-nums">
              {rollup.coverage.evaluated} of {rollup.coverage.total} datasets evaluated ·{' '}
              {rollup.coverage.attributed} attributed · {rollup.coverage.unattributed} unattributed
            </span>
            <button type="button" className="tri-xdr-text-btn" onClick={() => void exportCsv()}>
              Export CSV
            </button>
          </div>
          <p className="tri-xdr-rationale">{rollup.overall.rationale}</p>

          {rollup.pillars.map((p) => (
            <PillarRow key={p.pillar} pillar={p} />
          ))}

          <p className="tri-xdr-thresholds">
            Verdicts read against convergence ≥ {rollup.thresholds.convergence_systemic} and
            source-alignment ≥ {rollup.thresholds.source_alignment} — starting-point thresholds
            surfaced for field calibration, not truths.
          </p>
        </div>
      )}
    </section>
  );
}

export default CrossDatasetRollupSection;
